/**
 * Click-to-sort for every table in the app — the state, the click rule, and the
 * in-browser row ordering. Plain TS so a test can call it without a DOM
 * (`code/logic-outside-the-host.md`); the heading that renders it is
 * `ui-kit/molecules/sort-header.tsx`.
 *
 * The click rule:
 *  - clicking the sorted column FLIPS its direction — there is no third
 *    "back to default" click;
 *  - clicking a new column starts DESCENDING when it holds a date (newest
 *    first), ASCENDING otherwise (A→Z, lowest first);
 *  - a table whose default order is on a visible column shows that column as
 *    sorted, exactly as if it had been clicked. A default order on nothing
 *    visible is `key: null` — no arrow is active.
 *
 * ⚠️ `sortRows` is for a table that HOLDS EVERY ROW of its result. A paged or
 * capped list must sort on the server: reordering the page in hand shows a
 * "sorted" page that disagrees with every other page.
 */

export const SORT_DIRS = ['asc', 'desc'] as const;
export type SortDir = (typeof SORT_DIRS)[number];

/** `key: null` = the table's own default order, on no visible column. */
export type SortState<K extends string> = { key: K | null; dir: SortDir };

/**
 * The state after a heading click.
 *
 * `isDate` names the columns that hold a date; only they start descending.
 */
export function nextSort<K extends string>(
  current: SortState<K>,
  key: K,
  isDate: (key: K) => boolean = () => false,
): SortState<K> {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: isDate(key) ? 'desc' : 'asc' };
}

/** What a column's `<th aria-sort>` announces. */
export function ariaSortFor<K extends string>(
  key: K,
  sort: SortState<K>,
): 'ascending' | 'descending' | 'none' {
  if (sort.key !== key) return 'none';
  return sort.dir === 'asc' ? 'ascending' : 'descending';
}

/**
 * A comparable value for one cell. A `Date` and an ISO string both work; an
 * ISO-8601 string orders correctly as text.
 */
export type SortValue = string | number | bigint | boolean | Date | null | undefined;

/**
 * Values for the sortable columns of one row: one function per column, or one
 * function taking the column — for a table whose columns are only known at
 * runtime.
 */
export type SortAccessors<T, K extends string> =
  | Record<K, (row: T) => SortValue>
  | ((row: T, key: K) => SortValue);

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

function isBlank(v: SortValue): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '')
    || (typeof v === 'number' && Number.isNaN(v));
}

function compareValues(a: SortValue, b: SortValue): number {
  if (a instanceof Date) a = a.getTime();
  if (b instanceof Date) b = b.getTime();
  if (typeof a === 'boolean') a = a ? 1 : 0;
  if (typeof b === 'boolean') b = b ? 1 : 0;
  if (typeof a === 'string' && typeof b === 'string') return collator.compare(a, b);
  if ((typeof a === 'number' || typeof a === 'bigint') && (typeof b === 'number' || typeof b === 'bigint')) {
    return a < b ? -1 : a > b ? 1 : 0;
  }
  // Mixed kinds (a number next to text): compare as text, deterministically.
  return collator.compare(String(a), String(b));
}

/**
 * `rows` in the order `sort` asks for, as a NEW array.
 *
 * - `key: null` returns the rows in the order they came.
 * - **Blanks sort LAST in both directions.** A column flipped to descending
 *   should open on its largest real value, not on a screen of em-dashes.
 * - **Stable:** rows that tie keep their incoming order, so the server's own
 *   ordering is the tiebreak and a re-click never shuffles equal rows.
 * - Text compares case-insensitively with numbers inside it read as numbers
 *   ("Plan 2" before "Plan 10").
 */
export function sortRows<T, K extends string>(
  rows: readonly T[],
  sort: SortState<K>,
  accessors: SortAccessors<T, K>,
): T[] {
  const key = sort.key;
  if (key === null) return [...rows];
  const get = typeof accessors === 'function'
    ? (row: T) => accessors(row, key)
    : accessors[key];
  const sign = sort.dir === 'asc' ? 1 : -1;
  return rows
    .map((row, i) => ({ row, i, v: get(row) }))
    .sort((x, y) => {
      const xb = isBlank(x.v), yb = isBlank(y.v);
      if (xb || yb) return xb === yb ? x.i - y.i : xb ? 1 : -1;
      return sign * compareValues(x.v, y.v) || x.i - y.i;
    })
    .map((e) => e.row);
}

/**
 * The `sort` + `dir` query parameters for a list the SERVER sorts
 * (admin-api `services/list-sort.ts`). Empty for the default order on no
 * column, so the server applies its own default.
 */
export function sortParams<K extends string>(sort: SortState<K>): Record<string, string> {
  return sort.key === null ? {} : { sort: sort.key, dir: sort.dir };
}
